import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Sparkle, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

export default function Login() {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingApproval, setPendingApproval] = useState(false);

  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (mode === "register") {
      try {
        await api.post("/auth/register", { email, password, name });
        setPendingApproval(true);
        toast.success("Account created — awaiting admin approval");
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || "Registration failed");
      } finally { setLoading(false); }
      return;
    }
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back");
      navigate("/");
    } else {
      toast.error(res.error || "Failed");
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left panel — brand */}
      <div className="hidden lg:flex w-1/2 bg-slate-900 text-white flex-col justify-between p-12">
        <div className="flex items-center">
          <img
            src="/fanware-logo.webp"
            alt="Fanware Today"
            className="h-10 w-auto max-w-[260px] object-contain"
            data-testid="login-logo"
          />
        </div>

        <div className="max-w-md">
          <div className="text-xs uppercase tracking-widest text-blue-400 mb-3">Campaign OS</div>
          <h1 className="font-heading text-5xl font-bold leading-tight mb-4">
            Manage every influencer. Pay on time. Ship faster.
          </h1>
          <p className="text-slate-400 text-base leading-relaxed">
            Retire the spreadsheet. Track outreach, scripts, live posts, invoices and
            payments in one clean workspace built for your team.
          </p>
        </div>

        <div className="text-xs text-slate-500">
          Kollab · A workspace for creator campaigns
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-white">
        <div className="w-full max-w-sm">
          {pendingApproval ? (
            <div data-testid="pending-approval-state">
              <div className="w-12 h-12 rounded-md bg-amber-50 flex items-center justify-center mb-4">
                <CheckCircle size={22} className="text-amber-600" weight="duotone" />
              </div>
              <h2 className="font-heading text-3xl font-bold text-slate-900">Awaiting approval</h2>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                Your account for <span className="font-medium text-slate-900">{email}</span> is created. An admin needs to approve it before you can sign in.
              </p>
              <button
                onClick={() => { setPendingApproval(false); setMode("login"); }}
                className="mt-6 text-sm font-medium text-blue-600 hover:text-blue-700"
                data-testid="pending-back-to-login"
              >
                Back to sign in →
              </button>
            </div>
          ) : (
            <>
          <div className="mb-8">
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">
              {mode === "login" ? "Welcome back" : "Get started"}
            </div>
            <h2 className="font-heading text-3xl font-bold text-slate-900">
              {mode === "login" ? "Sign in to FT System" : "Request access"}
            </h2>
            {mode === "register" && (
              <p className="text-sm text-slate-500 mt-2">
                Create an account. An admin will approve access before you can sign in.
              </p>
            )}
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label htmlFor="name" className="text-xs uppercase tracking-wider text-slate-500">Name</Label>
                <Input
                  id="name"
                  data-testid="register-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="mt-1"
                />
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-slate-500">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@agency.com"
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-slate-500">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="mt-1"
              />
              {mode === "login" && (
                <div className="text-right mt-1.5">
                  <a
                    href="/forgot-password"
                    className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </a>
                </div>
              )}
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white h-11"
            >
              {loading ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-slate-500">
            {mode === "login" ? (
              <>
                No account yet?{" "}
                <button
                  onClick={() => setMode("register")}
                  data-testid="switch-to-register"
                  className="text-blue-600 hover:text-blue-700 font-medium"
                >
                  Create one
                </button>
              </>
            ) : (
              <>
                Already have one?{" "}
                <button
                  onClick={() => setMode("login")}
                  data-testid="switch-to-login"
                  className="text-blue-600 hover:text-blue-700 font-medium"
                >
                  Sign in
                </button>
              </>
            )}
          </div>

            </>
          )}
        </div>
      </div>
    </div>
  );
}
